/** @jsxImportSource @emotion/react */
import React, { memo, useCallback, useContext, useMemo } from 'react'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import _isFunction from 'lodash/isFunction'
import { parse } from 'query-string'
import CommonChrome from './CommonChrome'
import { RouteChangeHocExtraPropsType } from '@signal/components/ModalPrompt'
import UI_BASE_PATH from '@purecarslabs/signal-core/baseline/app/base-path'
import SWRRoot from '@purecarslabs/signal-core/swr/Root'
import { getLogoutRedirectUrl } from '@purecarslabs/signal-core/auth/utils/redirects'
import {
  AccountChangeHandlerType,
  UserAccountType
} from '@signal/core/user/types'
import withCommonSnapshotsOnly from '@signal/hoc/withCommonSnapshotsOnly'
import isDevelopment from '@purecarslabs/signal-utils/env/isDevelopment'
import isRelativeUrl from '@purecarslabs/signal-utils/isRelativeUrl'
import applyMiddleware from '@purecarslabs/signal-core/baseline/data/middleware/apply-middleware'
import fetchWithCredentials from '@purecarslabs/signal-core/baseline/data/utils/fetch-with-credentials'
import { HeaderWrapper } from './styles'
import { ACCOUNT_ID_REDIR_SUBST } from '@signal/core/router/redirect'
import { UseDynamicPathReplacementHookType } from '@signal/core/router/redirect/types'
import { ModalPromptContext } from '@purecarslabs/signal-components/ModalPrompt'
import isCurrentPathForAnonymousAccess from '@signal/utils/isAnonymousPath'
import renderIf from '@signal/utils/renderIf'

const middlewareFetcher = applyMiddleware()(fetchWithCredentials)

export type ChromeHeaderPropsType = {
  appMinWidth?: number
  children?: React.ReactNode
  hideChrome?: boolean
  hideFooter?: boolean
  hideMenu?: boolean
  routeRedirect?: string
  standardRedirect?: string
  onNeedOnAccountChange: (onAccountChange: AccountChangeHandlerType) => void
  useDynamicPathReplacementHook?: UseDynamicPathReplacementHookType
  title?: string | ((props: Record<string, unknown>) => string)
}

export const ChromeHeader: React.FC<ChromeHeaderPropsType> = ({
  appMinWidth,
  children,
  hideChrome,
  hideFooter,
  hideMenu,
  title,
  standardRedirect = `/${ACCOUNT_ID_REDIR_SUBST}`,
  routeRedirect,
  onNeedOnAccountChange,
  useDynamicPathReplacementHook,
  ...rest
}) => {
  const history = useHistory()
  const location = useLocation() as unknown as Location
  const params = useParams()
  // @ts-ignore
  const { accountId } = params
  const handleLogout = useCallback(
    () => window.location.assign(getLogoutRedirectUrl()),
    []
  )
  const titleText = _isFunction(title) ? title(params) : title
  const dynamicSearchUrl = useDynamicPathReplacementHook?.()
  const dynamicRedirectUrl = useMemo(() => {
    if (dynamicSearchUrl) {
      const searchPos = dynamicSearchUrl.indexOf('?')
      if (searchPos > -1) {
        const qp = new URLSearchParams(dynamicSearchUrl.substring(searchPos))
        if (qp.has('redirect_encoded')) {
          const value = qp.get('redirect_encoded')!
          const url = atob(decodeURIComponent(value))
          const origin = window.location.origin
          let originClip = origin.length
          if (!isDevelopment()) {
            // skip over the middle URL layers which come right after the domain
            // move the clip point past to the 3rd slash after the domain, e.g. <domain>/accounts/ui/<rest of url>
            originClip = url.indexOf(
              // this indexOf resolves to the third slash
              '/',
              url.indexOf(
                // this indexOf resolves to the second slash
                '/',
                originClip + 1
              ) + 1 // first slash should be at originClip
            )
            if (originClip == -1) {
              originClip = origin.length
            }
          }
          return url.substring(originClip)
        }
      }
    }
  }, [dynamicSearchUrl])

  const handleAccountChange = useCallback(
    ({ accountId }: UserAccountType) => {
      // @ts-ignore
      history.everNavigated = true

      const { redirect } = parse(location.search)
      if (redirect) {
        // supply the correct data on the redirect URL
        // @ts-ignore
        history.push(redirect)
      } else {
        if (routeRedirect) {
          // if per-route redirect is defined, use that
          history.push(routeRedirect.replace(ACCOUNT_ID_REDIR_SUBST, accountId))
        } else if (dynamicRedirectUrl) {
          history.push(
            dynamicRedirectUrl.replace(ACCOUNT_ID_REDIR_SUBST, accountId)
          )
        } else {
          // if no per-route redirect is defined, use the standard redirect
          history.push(
            standardRedirect.replace(ACCOUNT_ID_REDIR_SUBST, accountId)
          )
        }
      }
    },
    [
      dynamicRedirectUrl,
      history,
      location.search,
      routeRedirect,
      standardRedirect
    ]
  )

  if (onNeedOnAccountChange) {
    onNeedOnAccountChange(handleAccountChange)
  }

  const { invokeRouteChangeRequest } = useContext(
    ModalPromptContext
  ) as RouteChangeHocExtraPropsType

  const handleNavigate = useCallback(
    (link: string) => {
      if (invokeRouteChangeRequest && !isRelativeUrl(link)) {
        const url = new URL(link)
        // we can't get custom modal prompt styling because this will cause a page unload
        // so check to see if we have custom modal prompts to fire.  If there are none, or they are all
        // accepted, then the callback will be triggered to do the navigation
        invokeRouteChangeRequest(url, () => {
          window.open(url.href, '_self')
        })
        return
      }
      // @ts-ignore
      history.everNavigated = true
      history.push(link)
    },
    [history, invokeRouteChangeRequest]
  )

  const swrFetcher = useMemo(
    () =>
      middlewareFetcher({
        redirectOnError: accountId
          ? `${UI_BASE_PATH}/${accountId}/error`
          : `${UI_BASE_PATH}/error`
      }),
    [accountId]
  )

  return (
    <HeaderWrapper data-testid="chromeHeader">
      <SWRRoot fetcher={swrFetcher}>
        {renderIf(
          !isCurrentPathForAnonymousAccess(),
          () => (
            <CommonChrome
              accountId={accountId}
              appMinWidth={appMinWidth}
              hideChrome={hideChrome}
              hideFooter={hideFooter}
              hideMenu={hideMenu}
              location={location}
              title={titleText}
              onLogout={handleLogout}
              onAccountChange={handleAccountChange}
              onNavigate={handleNavigate}
              useDynamicPathReplacementHook={useDynamicPathReplacementHook}
              {...rest}
            >
              {children}
            </CommonChrome>
          ),
          () => children
        )}
      </SWRRoot>
    </HeaderWrapper>
  )
}

export default memo(withCommonSnapshotsOnly(ChromeHeader))
